import { useState, useRef, useEffect } from "react";
import {
  IoPlayOutline,
  IoPauseOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
} from "react-icons/io5";

interface AudioPlayerProps {
  audioUrl: string;
  isLoading?: boolean;
  error?: any;
  onRefreshUrl?: () => void;
  urlTimestamp?: number; // When the URL was fetched
}

const AudioPlayer = ({
  audioUrl,
  isLoading,
  error,
  onRefreshUrl,
  urlTimestamp,
}: AudioPlayerProps) => {
  // Audio player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);

  // Safe volume setter that ensures bounds
  const setSafeVolume = (newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
  };
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragType, setDragType] = useState<"progress" | "volume" | null>(null);
  const [urlExpired, setUrlExpired] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check if URL has expired based on timestamp (1 hour = 3600000 ms)
  const isUrlExpired = () => {
    if (!urlTimestamp) return false;
    const now = Date.now();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    return now - urlTimestamp > oneHour;
  };

  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);

  // Helper function to format duration
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // Audio player functions
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioError = (
    e: React.SyntheticEvent<HTMLAudioElement, Event>
  ) => {
    const audio = e.currentTarget;
    const error = audio.error;

    if (error) {
      console.error("Audio error:", error);

      // Check if it's a network error (likely expired URL)
      if (
        error.code === MediaError.MEDIA_ERR_NETWORK ||
        error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
      ) {
        // Automatically try to refresh the URL
        handleRefreshUrl();
      }
    }
  };

  // Handle fetch errors (for more detailed error detection)
  const handleAudioLoadError = async () => {
    try {
      // Try to fetch the URL to get more detailed error info
      const response = await fetch(audioUrl);
      if (!response.ok) {
        const text = await response.text();
        // Check if it's the specific AccessDenied error
        if (
          text.includes("AccessDenied") &&
          text.includes("Request has expired")
        ) {
          console.log("URL expired, refreshing...");
          handleRefreshUrl();
          return;
        }
      }
    } catch (error) {
      console.error("Error checking URL:", error);
      // If we can't check, assume it's expired and refresh
      handleRefreshUrl();
    }
  };

  const handleRefreshUrl = async () => {
    if (onRefreshUrl && !isRefreshing) {
      setIsRefreshing(true);
      setUrlExpired(false);
      try {
        await onRefreshUrl();
      } catch (error) {
        console.error("Failed to refresh URL:", error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = Math.max(
        0,
        Math.min(duration, (clickX / width) * duration)
      );
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && volumeRef.current) {
      const rect = volumeRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newVolume = Math.max(0, Math.min(1, clickX / width));
      setSafeVolume(newVolume);
      audioRef.current.volume = newVolume;
    }
  };

  // Drag functionality
  const handleMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    type: "progress" | "volume"
  ) => {
    e.preventDefault();
    setIsDragging(true);
    setDragType(type);

    if (type === "progress") {
      handleProgressClick(e);
    } else {
      handleVolumeClick(e);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !dragType) return;

    if (dragType === "progress" && audioRef.current && progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = Math.max(0, Math.min(1, clickX / width)) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    } else if (dragType === "volume" && audioRef.current && volumeRef.current) {
      const rect = volumeRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newVolume = Math.max(0, Math.min(1, clickX / width));
      setSafeVolume(newVolume);
      audioRef.current.volume = newVolume;
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragType(null);
  };

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragType, duration]);

  const togglePlaybackRate = () => {
    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    const newRate = rates[nextIndex];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  // Reset audio when URL changes
  useEffect(() => {
    if (audioRef.current) {
      setCurrentTime(0);
      setIsPlaying(false);
      setDuration(0);
      setUrlExpired(false);
    }
  }, [audioUrl]);

  // Check if URL has expired when component mounts or URL changes
  useEffect(() => {
    if (audioUrl && isUrlExpired()) {
      setUrlExpired(true);
      setIsPlaying(false);
    }
  }, [audioUrl, urlTimestamp]);

  // Check URL validity when component mounts
  useEffect(() => {
    if (audioUrl) {
      handleAudioLoadError();
    }
  }, [audioUrl]);

  // Set initial volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, volume));
    }
  }, [volume]);

  if (isLoading) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
        Loading audio recording...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg text-center text-red-500">
        Error loading audio recording
      </div>
    );
  }

  if (urlExpired) {
    return (
      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="text-center">
          <p className="text-yellow-700 mb-3">
            Audio link has expired. Click to refresh.
          </p>
          <button
            onClick={handleRefreshUrl}
            disabled={isRefreshing}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRefreshing ? "Refreshing..." : "Refresh Audio Link"}
          </button>
        </div>
      </div>
    );
  }

  if (!audioUrl) {
    return null;
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        preload="metadata"
        onError={handleAudioError}
      />

      <div className="flex items-center gap-3">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlayPause}
          className="text-gray-700 hover:text-gray-900 transition-colors"
        >
          {isPlaying ? (
            <IoPauseOutline size={20} />
          ) : (
            <IoPlayOutline size={20} />
          )}
        </button>

        {/* Progress Bar */}
        <div
          ref={progressRef}
          className={`flex-1 h-1 bg-gray-300 rounded-full relative select-none ${
            isDragging && dragType === "progress"
              ? "cursor-grabbing"
              : "cursor-pointer"
          }`}
          onClick={handleProgressClick}
          onMouseDown={(e) => handleMouseDown(e, "progress")}
        >
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-100"
            style={{
              width: `${duration ? (currentTime / duration) * 100 : 0}%`,
            }}
          />
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md border border-gray-300"
            style={{
              left: `${duration ? (currentTime / duration) * 100 : 0}%`,
              marginLeft: "-6px",
            }}
          />
        </div>

        {/* Current Time */}
        <span className="text-sm text-gray-700 min-w-[40px]">
          {formatDuration(Math.floor(currentTime))}
        </span>

        {/* Volume Icon */}
        {volume === 0 ? (
          <IoVolumeMuteOutline size={16} className="text-gray-700" />
        ) : (
          <IoVolumeHighOutline size={16} className="text-gray-700" />
        )}

        {/* Volume Slider */}
        <div
          ref={volumeRef}
          className={`w-16 h-1 bg-gray-300 rounded-full relative select-none ${
            isDragging && dragType === "volume"
              ? "cursor-grabbing"
              : "cursor-pointer"
          }`}
          onClick={handleVolumeClick}
          onMouseDown={(e) => handleMouseDown(e, "volume")}
        >
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-100"
            style={{ width: `${volume * 100}%` }}
          />
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md border border-gray-300"
            style={{ left: `${volume * 100}%`, marginLeft: "-6px" }}
          />
        </div>

        {/* Playback Speed */}
        <button
          onClick={togglePlaybackRate}
          className="text-sm text-gray-700 bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded transition-colors"
        >
          {playbackRate}x
        </button>
      </div>
    </div>
  );
};

export default AudioPlayer;
